uniform float pointSize;
uniform int colorMode;
uniform int classificationMask[8];
uniform vec3 colorRamp[32];
uniform int colorRampLength;
uniform sampler2D indexToColorLookup;
uniform float intensityRange[2];
uniform float zRange[2];
uniform float elevationRange[2];
uniform float datasetColor[3];

uniform vec3 classificationColors[32];
uniform int classificationColorsLength;

uniform uint filteredPoseId[256];
uniform uint filteredPoseIdLength;

uniform float split;
uniform float nodeIndex;

uniform int objectIdFilter[256];
uniform int objectIdFilterLength;
uniform float hagRange[2];
uniform float hagFilterRange[2];

#define MAX_CLIP_POLYGON 256
uniform vec2 clipPolygon[MAX_CLIP_POLYGON];
uniform int clipPolygonLength;

#include <common>
#include <clipping_planes_pars_vertex>
#include <logdepthbuf_pars_vertex>

in vec3 color;
// TODO: how to make classification come in as an int?
in float classification;
in int source_file_id;
in float intensity;
in float point_source_id;
in float extra_data;
in int object_id;
in float hag;

out vec4 vColor;
flat out int vDiscard;

// color ramps from https://www.shadertoy.com/view/WlfXRN (CC0 / Pulic Domain)

vec3 viridis(float t) {
    const vec3 c0 = vec3(0.2777273272234177, 0.005407344544966578, 0.3340998053353061);
    const vec3 c1 = vec3(0.1050930431085774, 1.404613529898575, 1.384590162594685);
    const vec3 c2 = vec3(-0.3308618287255563, 0.214847559468213, 0.09509516302823659);
    const vec3 c3 = vec3(-4.634230498983486, -5.799100973351585, -19.33244095627987);
    const vec3 c4 = vec3(6.228269936347081, 14.17993336680509, 56.69055260068105);
    const vec3 c5 = vec3(4.776384997670288, -13.74514537774601, -65.35303263337234);
    const vec3 c6 = vec3(-5.435455855934631, 4.645852612178535, 26.3124352495832);

    return c0+t*(c1+t*(c2+t*(c3+t*(c4+t*(c5+t*c6)))));
}

vec3 plasma(float t) {
    const vec3 c0 = vec3(0.05873234392399702, 0.02333670892565664, 0.5433401826748754);
    const vec3 c1 = vec3(2.176514634195958, 0.2383834171260182, 0.7539604599784036);
    const vec3 c2 = vec3(-2.689460476458034, -7.455851135738909, 3.110799939717086);
    const vec3 c3 = vec3(6.130348345893603, 42.3461881477227, -28.51885465332158);
    const vec3 c4 = vec3(-11.10743619062271, -82.66631109428045, 60.13984767418263);
    const vec3 c5 = vec3(10.02306557647065, 71.41361770095349, -54.07218655560067);
    const vec3 c6 = vec3(-3.658713842777788, -22.93153465461149, 18.19190778539828);

    return c0+t*(c1+t*(c2+t*(c3+t*(c4+t*(c5+t*c6)))));
}

vec3 magma(float t) {
    const vec3 c0 = vec3(-0.002136485053939582, -0.000749655052795221, -0.005386127855323933);
    const vec3 c1 = vec3(0.2516605407371642, 0.6775232436837668, 2.494026599312351);
    const vec3 c2 = vec3(8.353717279216625, -3.577719514958484, 0.3144679030132573);
    const vec3 c3 = vec3(-27.66873308576866, 14.26473078096533, -13.64921318813922);
    const vec3 c4 = vec3(52.17613981234068, -27.94360607168351, 12.94416944238394);
    const vec3 c5 = vec3(-50.76852536473588, 29.04658282127291, 4.23415299384598);
    const vec3 c6 = vec3(18.65570506591883, -11.48977351997711, -5.601961508734096);

    return c0+t*(c1+t*(c2+t*(c3+t*(c4+t*(c5+t*c6)))));
}

vec3 inferno(float t) {
    const vec3 c0 = vec3(0.0002189403691192265, 0.001651004631001012, -0.01948089843709184);
    const vec3 c1 = vec3(0.1065134194856116, 0.5639564367884091, 3.932712388889277);
    const vec3 c2 = vec3(11.60249308247187, -3.972853965665698, -15.9423941062914);
    const vec3 c3 = vec3(-41.70399613139459, 17.43639888205313, 44.35414519872813);
    const vec3 c4 = vec3(77.162935699427, -33.40235894210092, -81.80730925738993);
    const vec3 c5 = vec3(-71.31942824499214, 32.62606426397723, 73.20951985803202);
    const vec3 c6 = vec3(25.13112622477341, -12.24266895238567, -23.07032500287172);

    return c0+t*(c1+t*(c2+t*(c3+t*(c4+t*(c5+t*c6)))));
}

// Crossing-number point-in-polygon test against the selected clip ring.
bool insideClip(vec2 p) {
    bool inside = false;
    int j = clipPolygonLength - 1;
    for (int i = 0; i < MAX_CLIP_POLYGON; i++) {
        if (i >= clipPolygonLength) break;
        vec2 pi = clipPolygon[i];
        vec2 pj = clipPolygon[j];
        if (((pi.y > p.y) != (pj.y > p.y)) &&
            (p.x < (pj.x - pi.x) * (p.y - pi.y) / (pj.y - pi.y) + pi.x)) {
            inside = !inside;
        }
        j = i;
    }
    return inside;
}

vec3 rainbow(float t) {
    // Rainbow color ramp for elevation color mode
    // Colors: violet -> blue -> cyan -> green -> yellow -> orange -> red
    const vec3 c0 = vec3(0.278, 0.0, 0.714);   // violet at 0
    const vec3 c1 = vec3(0.0, 0.0, 1.0);       // blue at 1/6
    const vec3 c2 = vec3(0.0, 1.0, 1.0);       // cyan at 2/6
    const vec3 c3 = vec3(0.0, 1.0, 0.0);       // green at 3/6
    const vec3 c4 = vec3(1.0, 1.0, 0.0);       // yellow at 4/6
    const vec3 c5 = vec3(1.0, 0.64, 0.0);      // orange at 5/6
    const vec3 c6 = vec3(1.0, 0.0, 0.0);       // red at 1

    float t6 = t * 6.0;
    if (t6 < 1.0) return mix(c0, c1, t6);
    if (t6 < 2.0) return mix(c1, c2, t6 - 1.0);
    if (t6 < 3.0) return mix(c2, c3, t6 - 2.0);
    if (t6 < 4.0) return mix(c3, c4, t6 - 3.0);
    if (t6 < 5.0) return mix(c4, c5, t6 - 4.0);
    return mix(c5, c6, t6 - 5.0);
}



void main() {
	vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
	vec4 screenPosition = projectionMatrix * mvPosition;
	vec4 worldPosition = modelMatrix * vec4(position, 1.0);

	#include <clipping_planes_vertex>

	gl_Position = screenPosition;
	vDiscard = 0;
	gl_PointSize = pointSize;

	float absSplit = abs(split);
	if(absSplit > 0.001) {
		float ndcSplit = (absSplit - 0.5) * 2.0;
		float ndcX = screenPosition.x / screenPosition.w;
		float buffer = pointSize * 0.00033; // Magic number works with current point sizes

		if(split < 0.0 && (ndcSplit - buffer) < ndcX) {
			vDiscard = 1;
			return;
		} else if (split > 0.0 && (ndcSplit + buffer) > ndcX) {
			vDiscard = 1;
			return;
		}
	}

	int c = int(classification);
	if (((1 << c % 32) & classificationMask[c / 32]) == 0) {
		vColor = vec4(1.0, 1.0, 1.0, 1.0);
		vDiscard = 1;
	}

	if (filteredPoseIdLength > uint(0)) {
		bool keep = false;
		for (int i = 0; uint(i) < filteredPoseIdLength; i++) {
			if (point_source_id == float(filteredPoseId[i])) {
				keep = true;
			}
		}
		if (!keep) {
			vDiscard = 1;
			return;
		}
	}

	if (objectIdFilterLength > 0) {
		bool keep = false;
		for (int i = 0; i < 256; i++) {
			if (i >= objectIdFilterLength) break;
			if (object_id == objectIdFilter[i]) {
				keep = true;
				break;
			}
		}
		if (!keep) {
			vDiscard = 1;
			return;
		}
	}

	if (worldPosition.z < zRange[0] || worldPosition.z > zRange[1]) {
		vDiscard = 1;
		return;
	}

	if (hag < hagFilterRange[0] || hag > hagFilterRange[1]) {
		vDiscard = 1;
		return;
	}

	if (colorMode == 1) {
		// rgb
		vColor = vec4(color, 1.0);
	} else if (colorMode == 2) {
		// classification
		if (c < classificationColorsLength) {
		    vColor = vec4(classificationColors[c], 1.0);
		} else {
		    // fallback / default - translated from JS function
		    const float O = 0.25;
		    float x = classification / 16.0;
		    float r = 0.5 + 0.5 * cos(2.0 * 3.14159265359 * (2.0 * x + 0.0 + O));
		    float g = 0.5 + 0.5 * cos(2.0 * 3.14159265359 * (1.0 * x + 0.66 + O));
		    float b = 0.5 + 0.5 * cos(2.0 * 3.14159265359 * (1.0 * x + 0.33 + O));
		    vColor = vec4(r, g, b, 1.0);
		}
	} else if (colorMode == 3) {
		// intensity
		float gamma = 0.3;
		float scaledIntensity = (intensity - intensityRange[0]) / (intensityRange[1] - intensityRange[0]);
		vColor = vec4(vec3(pow(scaledIntensity, gamma)), 1.0);
	} else if (colorMode == 4) {
		// point source id
		vec3 sourceColor = colorRamp[int(point_source_id) % colorRampLength];
		vColor = vec4(mix(sourceColor, color, 0.33), 1.0);
	} else if (colorMode == 5) {
		// source file id
		vec3 metaColor = colorRamp[source_file_id % colorRampLength];
		vColor = vec4(mix(metaColor, color, 0.33), 1.0);
	} else if (colorMode == 6) {
		// dataset
		vColor = vec4(datasetColor[0]/255.0, datasetColor[1]/255.0, datasetColor[2]/255.0, 1.0);
	} else if (colorMode == 7) {
		// extra data
		// TODO: this shouldn't be hardcoded to 0-2000
		float v = extra_data * 65535.0 / 2000.0;

		vColor = vec4(plasma(v), 1.0);
		// vColor = inferno(v);
		// vColor = magma(v);
	} else if (colorMode == 8) {
    	// picking mode - encode vertex index as RGB
		int data = gl_VertexID;
    	float r = float((data >> 16) & 0xff) / 255.0;
    	float g = float((data >> 8) & 0xff) / 255.0;
    	float b = float(data & 0xff) / 255.0;
    	float a = nodeIndex / 255.0;
    	vColor = vec4(r, g, b, a);
	} else if (colorMode == 9) {
		// elevation
		float zMin = elevationRange[0];
		float zMax = elevationRange[1];
		float range = zMax - zMin;
		float normalizedZ = (range > 0.0) ? clamp((worldPosition.z - zMin) / range, 0.0, 1.0) : 0.5;
		vColor = vec4(rainbow(normalizedZ), 1.0);
	} else if (colorMode == 10) {
		// object
		if (object_id < 0) {
			vColor = vec4(1.0, 0.0, 1.0, 1.0);
		} else {
			vColor = vec4(colorRamp[object_id % colorRampLength], 1.0);
		}
	} else if (colorMode == 11) {
		// height above ground
		float hMin = hagRange[0];
		float hMax = hagRange[1];
		float range = hMax - hMin;

		if (range > 0.0) {
			float normalizedH = clamp((hag - hMin) / range, 0.0, 1.0);
			vColor = vec4(viridis(normalizedH), 1.0);
		} else {
			vColor = vec4(1.0, 0.0, 1.0, 1.0);
		}
	} else {
		vColor = vec4(0.81, 0.81, 0.81, 1.0);
	}

	// Dim points that fall outside the selected clip polygon (colorMode 8 is picking).
	if (clipPolygonLength > 0 && colorMode != 8 && !insideClip(worldPosition.xy)) {
		vColor.rgb *= 0.5;
	}

	#include <logdepthbuf_vertex>
}
